/**
 * Explore Line view (POS2-883, default): blue line + area gradient when unsplit; a
 * coloured line per split series otherwise. Weekend shading + weekday ticks on Daily
 * only. A single-bucket window can't draw a line, so it renders as a Bar (mock parity).
 */
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, XAxis, YAxis } from "recharts";

import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { useTranslation } from "@/i18n/use-translation";
import { EXPLORE_MEASURES, precedingWindow } from "@/lib/explore-engine";
import { weekendSpans } from "@/lib/trend-window";
import {
  EXPLORE_PALETTE,
  buildChartData,
  cellDeltas,
  formatBucketTick,
  isSingleBucketWindow,
  tickInterval,
  tickSpan,
  windowRowKeys,
} from "@/lib/explore-chart";

import { ExploreBar } from "./bar-view";
import {
  BucketTick,
  ExploreLegend,
  ExploreTooltip,
  seriesChartConfig,
  type ExploreViewProps,
} from "./chart-parts";

const AREA_COLOR = EXPLORE_PALETTE[0];
const AREA_GRADIENT_ID = "explore-area-fill";

export function ExploreLine(props: ExploreViewProps) {
  const { locale, t } = useTranslation();
  const { pivot, priorPivot, measure, currency, gran, split, window, timezone } = props;
  const { rows, series, unsplit } = buildChartData(
    pivot,
    measure,
    windowRowKeys(pivot, window, gran, timezone),
  );

  // A line needs at least two points; a single bucket is drawn as a Bar instead. The note that
  // accounts for it lives in the panel header (POS2-1050) — beside the title, so appearing and
  // disappearing never shifts the chart down (Artem). Same predicate on both sides.
  if (isSingleBucketWindow(pivot, window, gran, timezone)) return <ExploreBar {...props} />;

  const fmt = (n: number) => EXPLORE_MEASURES[measure].format(n, { currency, locale, t });
  const bands = gran === "daily" ? weekendSpans(rows.map((r) => r.key)) : [];
  const interval = tickInterval(rows.length);
  // Widen the tick labels to whatever calendar the plotted buckets span (POS2-1068).
  const span = tickSpan(rows.map((r) => r.key));
  // Per-series Δ from the shared derivation — same numbers as the Table, first-bucket baseline
  // from the preceding window included (POS2-1038).
  const deltas = cellDeltas(
    pivot,
    measure,
    window,
    gran,
    timezone,
    priorPivot,
    precedingWindow(window),
  );

  return (
    <div className="flex flex-col" data-testid="explore-view-line">
      <ChartContainer config={seriesChartConfig(series)} className="aspect-auto h-[420px] w-full">
        <ComposedChart data={rows} margin={{ left: 12, right: 12, top: 8 }}>
          <defs>
            <linearGradient id={AREA_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={AREA_COLOR} stopOpacity={0.16} />
              <stop offset="100%" stopColor={AREA_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} />
          {bands.map((b) => (
            <ReferenceArea
              key={b.x1}
              x1={b.x1}
              x2={b.x2}
              fill="rgba(17,24,39,0.045)"
              fillOpacity={1}
              ifOverflow="extendDomain"
            />
          ))}
          {gran === "daily" ? (
            <XAxis
              dataKey="key"
              tickLine={false}
              axisLine={false}
              interval={interval}
              height={44}
              tickMargin={6}
              padding={{ left: 14, right: 14 }}
              tick={<BucketTick span={span} />}
            />
          ) : (
            <XAxis
              dataKey="key"
              tickLine={false}
              axisLine={false}
              interval={interval}
              tickMargin={8}
              minTickGap={16}
              padding={{ left: 14, right: 14 }}
              tickFormatter={(v: string) => formatBucketTick(v, gran, span, { locale, t })}
            />
          )}
          <YAxis hide domain={[0, "auto"]} />
          <ChartTooltip
            content={<ExploreTooltip series={series} gran={gran} format={fmt} deltas={deltas} />}
          />
          {unsplit ? (
            <Area
              dataKey={series[0].key}
              type="linear"
              stroke={AREA_COLOR}
              strokeWidth={2.5}
              fill={`url(#${AREA_GRADIENT_ID})`}
              dot={false}
              isAnimationActive={false}
            />
          ) : (
            series.map((s) => (
              <Line
                key={s.key}
                dataKey={s.key}
                type="linear"
                stroke={s.color}
                strokeWidth={2.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))
          )}
        </ComposedChart>
      </ChartContainer>
      {split !== "none" && <ExploreLegend series={series} />}
    </div>
  );
}
